// Planta (PR 3, etapa 3.3). La recepción con el lector de mano y las tres
// bandejas que salen del punto de control: Flex sin «En camino», pendientes
// de geolocalización y códigos postales que faltan en el catálogo.
//
// Nada de lo que se muestra acá frena la operación: el escáner registra el
// ingreso igual y la alerta queda en su bandeja hasta que alguien la resuelva.

import React, { useCallback, useEffect, useState } from "react";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import { useAuth } from "../context/AuthContext";
import { requestJson } from "../lib/requestJson";
import { printPackageLabel } from "../lib/packageLabel";
import { ScanQueue, ScanRow, useScanQueue } from "../lib/useScanQueue";
import GeoResolutionPanel from "../components/warehouse/GeoResolutionPanel";

type Tab = "receive" | "flex" | "geo" | "postal" | "misplaced";
type Alert = { code: string; text: string; status_ml?: string | null; postal_code?: string | null; current?: string; suggested?: string };
type Receipt = {
  delivery: { id: number; tracking: string | number | null; store: string | null; receiver: string | null; direccion: string | null; postal_code: string | null; tipo_envio: string | null; is_turbo: boolean };
  shelf: { id: number; code: string; name: string; unclassified: boolean; suggested: string | null } | null;
  ops: { from: string; to: string; transitions: string[]; already_here: boolean };
  geo: { status: string; retried: boolean };
  flex: { checked: boolean; status_ml: string | null; expected: string; ok: boolean | null };
  alerts: Alert[];
};
type FlexRow = { id: number; tracking: string | number | null; store: string | null; receiver: string | null; is_turbo: boolean; ops_status: string; status_ml: string | null; pickup_at: string | null; shelf_code: string | null; collected_by: string | null };
type PostalRow = { code: string; deliveries: number; localidad: string | null; provincia: string | null };
type MisplacedRow = { id: number; tracking: string | number | null; lynx_code: string | null; store: string | null; receiver: string | null; postal_code_norm: string | null; codigo_postal: string | null; shelved_at: string | null; shelf_id: number; shelf_code: string; expected_shelf_id: number; expected_shelf_code: string; expected_unclassified: boolean };

const OPS_LABEL: Record<string, string> = {
  ENTERED: "Ingresado", COLLECTED: "Colectado", ARRIVED_WAREHOUSE: "En planta", SHELVED: "Estanteado",
  ASSIGNED_TO_ROUTE: "En recorrido", OUT_FOR_DELIVERY: "En calle", DELIVERED: "Entregado",
  NOT_DELIVERED: "No entregado", CANCELLED: "Cancelado",
};
const ML_LABEL: Record<string, string> = { shipped: "En camino", ready_to_ship: "Listo para enviar", handling: "En preparación", pending: "Pendiente", delivered: "Entregado", not_delivered: "No entregado", cancelled: "Cancelado" };
const mlLabel = (s: string | null | undefined) => (s ? ML_LABEL[s] ?? s : "sin estado");

/* ===== Estilos (mismo lenguaje que RoutesPage.tsx) ===== */
const titleStyle: React.CSSProperties = { margin: 0, fontSize: "24px", fontWeight: 700, color: "var(--text-primary)" };
const subtitleStyle: React.CSSProperties = { margin: "8px 0 0 0", color: "var(--text-secondary)", fontSize: "15px", lineHeight: 1.6 };
const smallMutedStyle: React.CSSProperties = { fontSize: "12px", color: "var(--text-secondary)" };
const tableStyle: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: "14px" };
const thStyle: React.CSSProperties = { textAlign: "left", padding: "10px 12px", borderBottom: "1px solid var(--border)", color: "var(--text-secondary)", fontWeight: 600, fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" };
const tdStyle: React.CSSProperties = { padding: "10px 12px", borderBottom: "1px solid var(--border)", color: "var(--text-primary)", verticalAlign: "top" };
const panelStyle: React.CSSProperties = { border: "1px solid var(--border)", borderRadius: "18px", background: "rgba(255,255,255,0.03)", padding: "14px" };
const badge = (color: string): React.CSSProperties => ({ display: "inline-block", padding: "3px 10px", borderRadius: "999px", fontSize: "12px", fontWeight: 600, border: `1px solid ${color}`, color });
const WARN = "var(--warning, #d29922)";

const alertColor = (code: string) => (code === "CLOSED" || code === "IN_ROUTE" ? "var(--danger)" : WARN);

export default function PlantaPage() {
  const { token } = useAuth();
  const [tab, setTab] = useState<Tab>("receive");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Lo escaneado vive acá y no dentro de la Recepción: ir a una bandeja y
  // volver desmontaba el panel y borraba la lista con la que el operador
  // controla lo que estanteó. La cola de escaneo también: lo que esté en
  // vuelo o esperando se sigue mandando aunque se cambie de bandeja.
  const [last, setLast] = useState<Receipt | null>(null);

  const req = useCallback((path: string, init: { method?: string; body?: unknown } = {}) => requestJson(path, { ...init, token }), [token]);

  const scan = useScanQueue<Record<string, string>, Receipt>(
    (body) => req("/v2/warehouse/receive", { method: "POST", body }),
    // Lo que quede sin respuesta (un 401 recarga hacia el login) vuelve a la
    // lista como «Sin confirmar» al volver a Planta.
    { onResult: (r) => { setLast(r); setError(null); }, onError: (m, code) => setError(`${code}: ${m}`), persistKey: "scan:planta" },
  );

  const flash = (msg: string) => { setNotice(msg); window.setTimeout(() => setNotice(null), 5000); };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", flexWrap: "wrap", alignItems: "flex-start" }}>
          <div>
            <h2 style={titleStyle}>Planta</h2>
            <p style={subtitleStyle}>
              Un escaneo registra el ingreso, indica la estantería y avisa lo que haya que mirar. Ninguna alerta frena el
              bulto: queda en su bandeja hasta que alguien la resuelva.
            </p>
          </div>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <Button variant={tab === "receive" ? "primary" : "secondary"} onClick={() => setTab("receive")}>Recepción</Button>
            <Button variant={tab === "flex" ? "primary" : "secondary"} onClick={() => setTab("flex")}>Flex sin «En camino»</Button>
            <Button variant={tab === "geo" ? "primary" : "secondary"} onClick={() => setTab("geo")}>Pendientes de geolocalización</Button>
            <Button variant={tab === "postal" ? "primary" : "secondary"} onClick={() => setTab("postal")}>CP no catalogados</Button>
            <Button variant={tab === "misplaced" ? "primary" : "secondary"} onClick={() => setTab("misplaced")}>Fuera de lugar</Button>
          </div>
        </div>
      </Card>

      {error ? <div style={{ ...panelStyle, borderColor: "var(--danger)", color: "var(--danger)" }}>{error}</div> : null}
      {notice ? <div style={{ ...panelStyle, borderColor: "var(--success)", color: "var(--success)" }}>{notice}</div> : null}

      {tab === "receive" ? <ReceivePanel scan={scan} token={token} onError={setError} last={last} /> : null}
      {tab === "flex" ? <FlexPanel req={req} onError={setError} onNotice={flash} /> : null}
      {tab === "geo" ? <GeoResolutionPanel req={req} onError={setError} onNotice={flash} /> : null}
      {tab === "postal" ? <PostalPanel req={req} onError={setError} onNotice={flash} /> : null}
      {tab === "misplaced" ? <MisplacedPanel req={req} onError={setError} onNotice={flash} /> : null}
    </div>
  );
}

/* ===== Recepción ===== */
function ReceivePanel({ scan, token, onError, last }: {
  scan: ScanQueue<Record<string, string>, Receipt>;
  token: string | null;
  onError: (m: string | null) => void;
  last: Receipt | null;
}) {
  // El lector de mano escribe el payload y manda Enter: el foco tiene que
  // volver al campo después de cada bulto, sin tocar el mouse.
  const focusScan = () => document.getElementById("planta-scan")?.focus();
  useEffect(() => { focusScan(); }, [last]);

  // El campo se vacía en el acto y el bulto entra a la cola (useScanQueue):
  // el siguiente se puede escanear sin esperar la respuesta.
  const submit = () => {
    // «LD:1600274» (número de LightData tipeado) no es solo dígitos: va como qr
    // y el servidor lo reconoce por el prefijo, sin confundirlo con un tracking.
    scan.enter((raw) => (/^\d+$/.test(raw) && raw.length < 8 ? { tracking: raw } : { qr: raw }));
    focusScan();
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.2fr) minmax(0, 1fr)", gap: "20px" }}>
      <Card>
        <div style={{ display: "flex", gap: "10px", alignItems: "end" }}>
          <div style={{ flex: 1 }}>
            <Input
              label="Escaneá la etiqueta o escribí el tracking (un número de LightData va con LD:, por ejemplo LD:1600274)"
              value={scan.value}
              onChange={(e: any) => scan.setValue(e.target.value)}
              onKeyDown={(e: any) => { if (e.key === "Enter") submit(); }}
              placeholder="QR de ML o de LightData, LP000000123, 43127654321 o LD:1600274"
              autoFocus
              id="planta-scan"
            />
          </div>
          <Button onClick={submit} disabled={!scan.value.trim()}>Recibir</Button>
        </div>
        <p style={{ ...smallMutedStyle, margin: "10px 0 0 0" }}>
          Si el envío no está en Lynx y el QR trae la cuenta, se da de alta en el acto. Un Flex se verifica contra Mercado Libre antes de estantear.
        </p>

        {last ? <ReceiptCard r={last} token={token} onError={onError} /> : (
          <div style={{ ...panelStyle, marginTop: "16px", color: "var(--text-secondary)" }}>Todavía no se escaneó nada en esta sesión.</div>
        )}
      </Card>

      <Card>
        <div style={{ ...smallMutedStyle, marginBottom: "8px", fontWeight: 600 }}>
          Escaneados en esta sesión · {scan.rows.length}{scan.pending ? ` · En cola: ${scan.pending}` : ""}
        </div>
        <div style={{ maxHeight: "560px", overflowY: "auto" }}>
          {scan.rows.length === 0 ? <div style={smallMutedStyle}>Nada todavía.</div> : scan.rows.map((row) => <SessionLine key={row.key} row={row} />)}
        </div>
      </Card>
    </div>
  );
}

// Una línea de «Escaneados en esta sesión». Si falló, va en rojo con el
// código tal como se escaneó, porque el campo ya se vació.
function SessionLine({ row }: { row: ScanRow<Receipt> }) {
  const s = row.result;
  return (
    <div data-testid="planta-session-row" style={{ display: "flex", gap: "10px", padding: "8px 0", borderBottom: "1px solid var(--border)", alignItems: "baseline", fontSize: "13px", color: s ? "var(--text-primary)" : "var(--danger)" }}>
      <span style={{ ...smallMutedStyle, width: "46px", flexShrink: 0 }}>{row.at.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}</span>
      {s ? (
        <>
          <span style={{ fontFamily: "monospace" }}>{s.delivery.tracking ?? s.delivery.id}</span>
          <span style={{ fontWeight: 700 }}>{s.shelf ? s.shelf.code : "—"}</span>
          <span style={smallMutedStyle}>{s.ops.already_here ? "ya estaba" : OPS_LABEL[s.ops.to] ?? s.ops.to}</span>
          {s.alerts.length ? <span style={badge(alertColor(s.alerts[0].code))}>{s.alerts.length} aviso{s.alerts.length > 1 ? "s" : ""}</span> : null}
        </>
      ) : (
        <>
          <span style={{ fontFamily: "monospace", wordBreak: "break-all" }}>{row.code}</span>
          <span>{row.error}</span>
        </>
      )}
    </div>
  );
}

// El resultado de un escaneo: la estantería en grande, para leerla a un metro.
function ReceiptCard({ r, token, onError }: { r: Receipt; token: string | null; onError: (m: string | null) => void }) {
  const shelfColor = !r.shelf ? "var(--danger)" : r.shelf.unclassified ? WARN : "var(--success)";
  return (
    <div style={{ ...panelStyle, marginTop: "16px", borderColor: shelfColor }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", flexWrap: "wrap", alignItems: "baseline" }}>
        <div>
          <div style={{ fontSize: "52px", fontWeight: 800, lineHeight: 1, color: shelfColor, fontFamily: "monospace" }}>{r.shelf ? r.shelf.code : "NO ENTRA"}</div>
          <div style={{ ...smallMutedStyle, marginTop: "6px" }}>
            {r.shelf ? r.shelf.name : "ver el aviso"}{r.shelf?.suggested ? ` · el mapa dice ${r.shelf.suggested}` : ""}
          </div>
        </div>
        <div style={{ textAlign: "right", color: "var(--text-primary)", fontSize: "14px" }}>
          <div style={{ fontFamily: "monospace", fontWeight: 700 }}>{r.delivery.tracking ?? r.delivery.id}</div>
          <div>{r.delivery.store ?? "—"}</div>
          <div style={smallMutedStyle}>{r.delivery.receiver ?? "—"} · {r.delivery.direccion ?? "sin dirección"} · CP {r.delivery.postal_code ?? "—"}</div>
          <div style={{ marginTop: "6px" }}>
            <span style={badge("var(--text-secondary)")}>{r.ops.already_here ? "ya estaba en planta" : `${OPS_LABEL[r.ops.from] ?? r.ops.from} → ${OPS_LABEL[r.ops.to] ?? r.ops.to}`}</span>
            {" "}
            {r.delivery.tipo_envio === "flex" ? <span style={badge(r.flex.ok ? "var(--success)" : WARN)}>Flex · {mlLabel(r.flex.status_ml)}</span> : null}
            {r.delivery.is_turbo ? <> <span style={badge("var(--primary, #4f8cff)")}>Turbo</span></> : null}
          </div>
          {/* Etiqueta propia: la de Mercado Libre ya viene pegada, esta es para
              el bulto que entro sin nada o con la etiqueta rota. */}
          <div style={{ marginTop: "10px" }}>
            <Button
              variant="secondary"
              onClick={async () => {
                onError(null);
                try {
                  await printPackageLabel(r.delivery.id, { token, scope: "warehouse" });
                } catch (e: any) { onError(e.message); }
              }}
            >
              {r.delivery.tracking ? "Imprimir etiqueta de Lynx" : "Imprimir etiqueta"}
            </Button>
          </div>
        </div>
      </div>
      {r.alerts.length ? (
        <div style={{ marginTop: "12px", display: "flex", flexDirection: "column", gap: "6px" }}>
          {r.alerts.map((a, i) => (
            <div key={i} style={{ color: alertColor(a.code), fontSize: "14px" }}>
              <b>{a.code.replace(/_/g, " ")}</b> — {a.text}
              {a.status_ml ? ` (figura «${mlLabel(a.status_ml)}»)` : ""}
              {a.postal_code ? ` (${a.postal_code})` : ""}
            </div>
          ))}
        </div>
      ) : <div style={{ marginTop: "12px", color: "var(--success)", fontSize: "14px" }}>Sin avisos.</div>}
    </div>
  );
}

/* ===== Flex sin «En camino» ===== */
function FlexPanel({ req, onError, onNotice }: { req: any; onError: (m: string | null) => void; onNotice: (m: string) => void }) {
  const [rows, setRows] = useState<FlexRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await req("/v2/warehouse/flex/not-shipped")).deliveries ?? []); }
    catch (e: any) { onError(e.message); } finally { setLoading(false); }
  }, [req, onError]);
  useEffect(() => { load(); }, [load]);

  const refresh = async (d: FlexRow) => {
    setBusyId(d.id);
    try {
      const json = await req(`/v2/deliveries/${d.id}/ml-refresh`, { method: "POST" });
      onNotice(`${d.tracking ?? d.id}: Mercado Libre informa «${mlLabel(json.status_ml)}»`);
      load();
    } catch (e: any) { onError(e.message); } finally { setBusyId(null); }
  };

  return (
    <Card>
      <p style={{ ...smallMutedStyle, margin: "0 0 12px 0" }}>
        Flex que ya salieron de la tienda y Mercado Libre todavía no muestra «En camino». Se regulariza en Envíos Flex;
        acá solo se vuelve a consultar el estado real. Lynx no escribe estados en Mercado Libre.
      </p>
      <div style={{ overflowX: "auto" }}>
        <table style={tableStyle}>
          <thead><tr>
            <th style={thStyle}>Tracking</th><th style={thStyle}>Tienda</th><th style={thStyle}>Destinatario</th>
            <th style={thStyle}>Colectó</th><th style={thStyle}>Estado Lynx</th><th style={thStyle}>Mercado Libre</th><th style={thStyle}></th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td style={tdStyle} colSpan={7}>Cargando…</td></tr>
              : rows.length === 0 ? <tr><td style={tdStyle} colSpan={7}>No hay Flex sin «En camino». Bien.</td></tr>
              : rows.map((d) => (
                <tr key={d.id}>
                  <td style={{ ...tdStyle, fontFamily: "monospace" }}>{d.tracking ?? d.id}{d.is_turbo ? " · turbo" : ""}</td>
                  <td style={tdStyle}>{d.store ?? "—"}</td>
                  <td style={tdStyle}>{d.receiver ?? "—"}</td>
                  <td style={tdStyle}>{d.collected_by ?? "—"}<br /><span style={smallMutedStyle}>{d.pickup_at ? new Date(d.pickup_at).toLocaleString("es-AR") : ""}</span></td>
                  <td style={tdStyle}>{OPS_LABEL[d.ops_status] ?? d.ops_status}{d.shelf_code ? ` · ${d.shelf_code}` : ""}</td>
                  <td style={tdStyle}><span style={badge(WARN)}>{mlLabel(d.status_ml)}</span></td>
                  <td style={tdStyle}><Button variant="secondary" onClick={() => refresh(d)} disabled={busyId === d.id}>{busyId === d.id ? "…" : "Reconsultar"}</Button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}


/* ===== CP no catalogados ===== */
function PostalPanel({ req, onError, onNotice }: { req: any; onError: (m: string | null) => void; onNotice: (m: string) => void }) {
  const [rows, setRows] = useState<PostalRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await req("/v2/deliveries/geo/unknown-postal")).postal_codes ?? []); }
    catch (e: any) { onError(e.message); } finally { setLoading(false); }
  }, [req, onError]);
  useEffect(() => { load(); }, [load]);

  const add = async (code: string) => {
    setBusy(code);
    try {
      await req("/v2/deliveries/geo/postal-codes", { method: "POST", body: { code } });
      onNotice(`CP ${code} agregado al catálogo. La tarifa y la estantería se cargan aparte.`);
      load();
    } catch (e: any) { onError(e.message); } finally { setBusy(null); }
  };

  return (
    <Card>
      <p style={{ ...smallMutedStyle, margin: "0 0 12px 0" }}>
        Códigos postales legibles que todavía no están en el catálogo geográfico. No son pendientes de geolocalización:
        el envío sabe dónde va. Agregarlos al catálogo no toca tarifas ni cobertura Flex, ni los manda a una estantería.
      </p>
      <div style={{ overflowX: "auto" }}>
        <table style={tableStyle}>
          <thead><tr><th style={thStyle}>Código postal</th><th style={thStyle}>Localidad</th><th style={thStyle}>Envíos</th><th style={thStyle}></th></tr></thead>
          <tbody>
            {loading ? <tr><td style={tdStyle} colSpan={4}>Cargando…</td></tr>
              : rows.length === 0 ? <tr><td style={tdStyle} colSpan={4}>Todos los códigos postales en uso están en el catálogo.</td></tr>
              : rows.map((r) => (
                <tr key={r.code}>
                  <td style={{ ...tdStyle, fontFamily: "monospace", fontWeight: 700 }}>{r.code}</td>
                  <td style={tdStyle}>{[r.localidad, r.provincia].filter(Boolean).join(", ") || "—"}</td>
                  <td style={tdStyle}>{r.deliveries}</td>
                  <td style={tdStyle}><Button variant="secondary" onClick={() => add(r.code)} disabled={busy === r.code}>Agregar al catálogo</Button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ===== Fuera de lugar ===== */
// Bultos estanteados donde no les toca por su código postal. Hasta acá el
// desvío solo se veía reescaneando el bulto; y se llega ahí sin reescaneo
// porque el operario lo apoyó mal, o porque se remapeó el CP después.
// Importa porque el reparto se arma por estantería física.
function MisplacedPanel({ req, onError, onNotice }: { req: any; onError: (m: string | null) => void; onNotice: (m: string) => void }) {
  const [rows, setRows] = useState<MisplacedRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await req("/v2/warehouse/misplaced")).deliveries ?? []); }
    catch (e: any) { onError(e.message); } finally { setLoading(false); }
  }, [req, onError]);
  useEffect(() => { load(); }, [load]);

  // El operario lo lleva físicamente a la estantería que le toca y recién
  // entonces lo confirma acá: la ubicación física manda, la pantalla la sigue.
  const mover = async (d: MisplacedRow) => {
    setBusyId(d.id);
    try {
      await req(`/v2/warehouse/deliveries/${d.id}/shelf`, { method: "PUT", body: { shelf_id: d.expected_shelf_id } });
      onNotice(`${d.tracking ?? d.lynx_code ?? d.id}: movido de ${d.shelf_code} a ${d.expected_shelf_code}`);
      load();
    } catch (e: any) { onError(e.message); } finally { setBusyId(null); }
  };

  return (
    <Card>
      <p style={{ ...smallMutedStyle, margin: "0 0 12px 0" }}>
        Bultos que están en una estantería distinta de la que les toca por su código postal, ya sea porque se apoyaron donde no era o
        porque el mapa cambió después. El reparto se arma por estantería física: si no se corrige, salen con el recorrido equivocado.
        Llevalo a la estantería que le toca y confirmalo acá.
      </p>
      <div style={{ overflowX: "auto" }}>
        <table style={tableStyle}>
          <thead><tr>
            <th style={thStyle}>Bulto</th><th style={thStyle}>Tienda</th><th style={thStyle}>Destinatario</th><th style={thStyle}>CP</th>
            <th style={thStyle}>Está en</th><th style={thStyle}>Le toca</th><th style={thStyle}>Estanteado</th><th style={thStyle}></th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td style={tdStyle} colSpan={8}>Cargando…</td></tr>
              : rows.length === 0 ? <tr><td style={tdStyle} colSpan={8}>Todo está donde le toca. Bien.</td></tr>
              : rows.map((d) => (
                <tr key={d.id}>
                  <td style={{ ...tdStyle, fontFamily: "monospace" }}>{d.tracking ?? d.lynx_code ?? d.id}</td>
                  <td style={tdStyle}>{d.store ?? "—"}</td>
                  <td style={tdStyle}>{d.receiver ?? "—"}</td>
                  <td style={{ ...tdStyle, fontFamily: "monospace" }}>{d.postal_code_norm ?? d.codigo_postal ?? "—"}</td>
                  <td style={tdStyle}><span style={badge(WARN)}>{d.shelf_code}</span></td>
                  <td style={tdStyle}><span style={badge(d.expected_unclassified ? "var(--text-secondary)" : "var(--success)")}>{d.expected_shelf_code}</span></td>
                  <td style={tdStyle}><span style={smallMutedStyle}>{d.shelved_at ? new Date(d.shelved_at).toLocaleString("es-AR") : "—"}</span></td>
                  <td style={tdStyle}><Button variant="secondary" onClick={() => mover(d)} disabled={busyId === d.id}>{busyId === d.id ? "…" : `Mover a ${d.expected_shelf_code}`}</Button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
